import { createSlice } from "@reduxjs/toolkit";
import {
  getPharmacyChatConversations,
  getPharmacyChatMessages,
  sendPharmacyChatMessage,
  createPharmacyChatOrder,
} from "../../reducer/pharmacyChatOrders/pharmacyChatOrdersReducer";

import i18n from "../../i18n";
const initialState = {
  conversations: [],
  conversationsLoading: false,
  messages: [],
  messagesLoading: false,
  sendingMessage: false,
  creatingOrder: false,
  selectedConversationKey: null,
};

const pharmacyChatOrdersSlice = createSlice({
  name: "pharmacyChatOrders",
  initialState,
  reducers: {
    setSelectedConversation: (state, action) => {
      state.selectedConversationKey = action.payload;
    },
    addIncomingMessage: (state, action) => {
      const msg = action.payload;
      // Add to messages if it belongs to the current conversation
      if (msg.conversation_key === state.selectedConversationKey) {
        const exists = state.messages.find((m) => m.id === msg.message_id);
        if (!exists) {
          state.messages.push({
            id: msg.message_id,
            sender_user_id: msg.sender_user_id,
            receiver_user_id: msg.receiver_user_id || null,
            message: msg.message,
            message_type: msg.message_type || "text",
            is_read: false,
            created_at: msg.created_at || new Date().toISOString(),
          });
        }
      }
      // Update conversation's last message and unread count
      const convIdx = state.conversations.findIndex(
        (c) => c.conversation_key === msg.conversation_key,
      );
      if (convIdx !== -1) {
        state.conversations[convIdx].last_message = msg.message;
        state.conversations[convIdx].last_message_at =
          msg.created_at || new Date().toISOString();
        if (msg.conversation_key !== state.selectedConversationKey) {
          state.conversations[convIdx].unread_count =
            (state.conversations[convIdx].unread_count || 0) + 1;
        }
      } else {
        // New conversation from a new patient
        state.conversations.unshift({
          conversation_key: msg.conversation_key,
          patient_user_id: msg.patient_user_id,
          pharmacy_user_id: msg.pharmacy_user_id,
          patient_name: msg.patient_name || i18n.t("chat.patient"),
          patient_avatar: null,
          last_message: msg.message,
          last_message_at: msg.created_at || new Date().toISOString(),
          unread_count: 1,
        });
      }
    },
    clearMessages: (state) => {
      state.messages = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPharmacyChatConversations.pending, (state) => {
        state.conversationsLoading = true;
      })
      .addCase(getPharmacyChatConversations.fulfilled, (state, action) => {
        state.conversationsLoading = false;
        state.conversations = action.payload?.data?.conversations || [];
      })
      .addCase(getPharmacyChatConversations.rejected, (state) => {
        state.conversationsLoading = false;
      })

      .addCase(getPharmacyChatMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(getPharmacyChatMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.messages = action.payload?.data?.messages || [];
      })
      .addCase(getPharmacyChatMessages.rejected, (state) => {
        state.messagesLoading = false;
      })

      .addCase(sendPharmacyChatMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(sendPharmacyChatMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const msg = action.payload?.data;
        if (msg) {
          state.messages.push({
            id: msg.message_id,
            sender_user_id: msg.sender_user_id,
            receiver_user_id: msg.receiver_user_id,
            message: msg.message,
            message_type: msg.message_type || "text",
            is_read: false,
            created_at: msg.created_at,
          });
        }
      })
      .addCase(sendPharmacyChatMessage.rejected, (state) => {
        state.sendingMessage = false;
      })

      .addCase(createPharmacyChatOrder.pending, (state) => {
        state.creatingOrder = true;
      })
      .addCase(createPharmacyChatOrder.fulfilled, (state) => {
        state.creatingOrder = false;
      })
      .addCase(createPharmacyChatOrder.rejected, (state) => {
        state.creatingOrder = false;
      });
  },
});

export const { setSelectedConversation, addIncomingMessage, clearMessages } =
  pharmacyChatOrdersSlice.actions;
export default pharmacyChatOrdersSlice.reducer;
